import { useEffect, useState } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
} from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { GOOGLE_PLACES_API_KEY } from "../../constants/ApiConfig";

interface AddressAutocompleteInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

interface PlacePrediction {
  placeId: string;
  description: string;
}

interface GooglePlacesAutocompleteSuggestion {
  placePrediction?: {
    placeId?: string;
    text?: {
      text?: string;
    };
  };
}

interface GooglePlacesAutocompleteResponse {
  suggestions?: GooglePlacesAutocompleteSuggestion[];
  error?: {
    code?: number;
    message?: string;
    status?: string;
  };
}

const minimumSearchLength = 3;

const AddressAutocompleteInput = ({
  label,
  value,
  onChange,
  placeholder,
  disabled,
}: AddressAutocompleteInputProps) => {
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!GOOGLE_PLACES_API_KEY) {
      setPredictions([]);
      return;
    }

    if (value.trim().length < minimumSearchLength) {
      setPredictions([]);
      return;
    }

    const timeout = window.setTimeout(async () => {
      try {
        const response = await fetch(
          "https://places.googleapis.com/v1/places:autocomplete",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Goog-Api-Key": GOOGLE_PLACES_API_KEY,
              "X-Goog-FieldMask":
                "suggestions.placePrediction.placeId,suggestions.placePrediction.text",
            },
            body: JSON.stringify({
              input: value.trim(),
              includedPrimaryTypes: ["street_address", "premise"],
              languageCode: "bs",
              regionCode: "ba",
            }),
          },
        );

        const data =
          (await response.json()) as GooglePlacesAutocompleteResponse;

        if (!response.ok || data.error) {
          setPredictions([]);
          return;
        }

        setPredictions(
          data.suggestions
            ?.map((suggestion) => {
              const placePrediction = suggestion.placePrediction;

              if (!placePrediction?.placeId || !placePrediction?.text?.text) {
                return null;
              }

              return {
                placeId: placePrediction.placeId,
                description: placePrediction.text.text,
              };
            })
            .filter(
              (prediction): prediction is PlacePrediction =>
                prediction !== null,
            ) || [],
        );
      } catch {
        setPredictions([]);
      }
    }, 180);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [value]);

  const showPredictions = focused && predictions.length > 0;

  const handleSelect = (prediction: PlacePrediction) => {
    onChange(prediction.description);
    setPredictions([]);
    setFocused(false);
  };

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <TextField
        fullWidth
        label={label}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          window.setTimeout(() => setFocused(false), 150);
        }}
      />

      {showPredictions ? (
        <Paper
          elevation={4}
          sx={{
            position: "absolute",
            zIndex: 30,
            top: "100%",
            left: 0,
            right: 0,
            mt: 0.5,
            maxHeight: 260,
            overflowY: "auto",
          }}
        >
          <List dense>
            {predictions.map((prediction) => (
              <ListItemButton
                key={prediction.placeId}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => handleSelect(prediction)}
              >
                <LocationOnOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                <ListItemText primary={prediction.description} />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      ) : null}
    </Box>
  );
};

export default AddressAutocompleteInput;
